import {
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import { Button } from '@rekrytering/ui/components/button'
import { Checkbox } from '@rekrytering/ui/components/checkbox'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@rekrytering/ui/components/dialog'
import { Input } from '@rekrytering/ui/components/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@rekrytering/ui/components/select'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CandidateNotesThread } from '@/features/candidates/components/candidate-notes-thread'
import { CandidateOfferPanel } from '@/features/candidates/components/candidate-offer-panel'
import { CandidateReferralSource } from '@/features/candidates/components/candidate-referral-source'
import { CandidateTagsEditor } from '@/features/candidates/components/candidate-tags-editor'
import { CandidateTodosPanel } from '@/features/candidates/components/candidate-todos-panel'
import { PipelineStageTimeline } from '@/features/candidates/components/pipeline-stage-timeline'
import { RichTextEditor } from '@/shared/ui/rich-text-editor'

type RejectDialogState =
  | { open: false }
  | { open: true; candidateId: Id<'candidates'> }

type BulkMessageDialogState = { open: false } | { open: true }

type CompareDialogState = { open: false } | { open: true }

type BulkSendResult =
  | { status: 'idle' }
  | { status: 'success'; sentCount: number; failedCount: number }

const MAX_COMPARE_CANDIDATES = 3

const PIPELINE_STAGES = [
  'applied',
  'screening',
  'interview',
  'offer',
  'hired',
  'rejected',
] as const

type PipelineStage = (typeof PIPELINE_STAGES)[number]

const PIPELINE_STAGE_LABELS: Record<PipelineStage, string> = {
  applied: 'Applied',
  screening: 'Screening',
  interview: 'Interview',
  offer: 'Offer',
  hired: 'Hired',
  rejected: 'Rejected',
}

const PIPELINE_STAGE_STYLES: Record<PipelineStage, string> = {
  applied: 'bg-gray-100 text-gray-700',
  screening: 'bg-blue-100 text-blue-700',
  interview: 'bg-purple-100 text-purple-700',
  offer: 'bg-yellow-100 text-yellow-700',
  hired: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
}

const CANDIDATE_STATUSES = [
  'invited',
  'started',
  'completed',
  'expired',
  'disqualified',
  'rejected',
  'advanced',
] as const

type CandidateStatus = (typeof CANDIDATE_STATUSES)[number]

const STATUS_LABELS: Record<CandidateStatus, string> = {
  invited: 'Invited',
  started: 'Started',
  completed: 'Completed',
  expired: 'Expired',
  disqualified: 'Disqualified',
  rejected: 'Rejected',
  advanced: 'Advanced',
}

export const CandidatesSearchPage = () => {
  const { t } = useTranslation()

  const { mutateAsync: movePipelineStage } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.candidates.moveMyPipelineStage,
    ),
  })

  const { mutateAsync: rejectCandidateWithReason } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.reject_reasons.rejectMyCandidateWithReason,
    ),
  })

  const { mutateAsync: sendBulkMessage, isPending: isSendingBulk } =
    useMutation({
      mutationFn: useConvexAction(
        api.features.candidates.send_bulk_message.sendMyBulkMessage,
      ),
    })

  const [inputValue, setInputValue] = useState('')
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined)
  const [selectedJobId, setSelectedJobId] = useState<Id<'jobs'> | undefined>(
    undefined,
  )
  const [selectedStatus, setSelectedStatus] = useState<
    CandidateStatus | undefined
  >(undefined)
  const [minScore, setMinScore] = useState<number | undefined>(undefined)
  const [maxScore, setMaxScore] = useState<number | undefined>(undefined)
  const [fromAt, setFromAt] = useState<number | undefined>(undefined)
  const [toAt, setToAt] = useState<number | undefined>(undefined)
  const [selectedTagId, setSelectedTagId] = useState<Id<'tags'> | undefined>(
    undefined,
  )
  const [expandedCandidateId, setExpandedCandidateId] = useState<
    Id<'candidates'> | undefined
  >(undefined)
  const [rejectDialog, setRejectDialog] = useState<RejectDialogState>({
    open: false,
  })
  const [selectedRejectReasonId, setSelectedRejectReasonId] = useState<
    Id<'rejectReasons'> | undefined
  >(undefined)
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<
    Set<Id<'candidates'>>
  >(new Set())
  const [bulkMessageDialog, setBulkMessageDialog] =
    useState<BulkMessageDialogState>({ open: false })
  const [compareDialog, setCompareDialog] = useState<CompareDialogState>({
    open: false,
  })
  const [bulkTemplateId, setBulkTemplateId] = useState<
    Id<'messageTemplates'> | undefined
  >(undefined)
  const [bulkSubject, setBulkSubject] = useState('')
  const [bulkBody, setBulkBody] = useState('')
  const [bulkEditorKey, setBulkEditorKey] = useState(0)
  const [bulkSendResult, setBulkSendResult] = useState<BulkSendResult>({
    status: 'idle',
  })

  const { data: rejectReasons = [] } = useQuery(
    convexQuery(api.features.candidates.reject_reasons.listMyRejectReasons, {}),
  )

  const { data: allTags = [] } = useQuery(
    convexQuery(api.features.candidates.tags.listMyTags, {}),
  )

  const { data: messageTemplates = [] } = useQuery(
    convexQuery(api.features.org.message_templates.listMyMessageTemplates, {}),
  )

  const selectedCandidateIdsArray = Array.from(selectedCandidateIds)
  const canCompare =
    selectedCandidateIdsArray.length >= 2 &&
    selectedCandidateIdsArray.length <= MAX_COMPARE_CANDIDATES

  const { data: comparisonData = [] } = useQuery(
    convexQuery(
      api.features.candidates.candidates_comparison.compareMySelectedCandidates,
      {
        candidateIds: compareDialog.open ? selectedCandidateIdsArray : [],
      },
    ),
  )

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const trimmed = inputValue.trim()
      setSearchQuery(trimmed.length > 0 ? trimmed : undefined)
    }, 300)
    return () => clearTimeout(timeoutId)
  }, [inputValue])

  const { data: jobs } = useQuery(
    convexQuery(api.features.jobs.jobs.listMyJobs, {}),
  )

  const { data: results, isLoading } = useQuery(
    convexQuery(api.features.candidates.candidates.searchMyCandidates, {
      searchQuery,
      jobId: selectedJobId,
      status: selectedStatus,
      minScore,
      maxScore,
      fromAt,
      toAt,
      tagId: selectedTagId,
    }),
  )

  const hasActiveFilters =
    searchQuery !== undefined ||
    selectedJobId !== undefined ||
    selectedStatus !== undefined ||
    minScore !== undefined ||
    maxScore !== undefined ||
    fromAt !== undefined ||
    toAt !== undefined ||
    selectedTagId !== undefined

  const handleClearFilters = (): void => {
    setInputValue('')
    setSearchQuery(undefined)
    setSelectedJobId(undefined)
    setSelectedStatus(undefined)
    setMinScore(undefined)
    setMaxScore(undefined)
    setFromAt(undefined)
    setToAt(undefined)
    setSelectedTagId(undefined)
  }

  const formatDate = (timestampMs: number): string => {
    return new Date(timestampMs).toLocaleDateString()
  }

  const handleTogglePipelineHistory = (candidateId: Id<'candidates'>): void => {
    setExpandedCandidateId((prev) =>
      prev === candidateId ? undefined : candidateId,
    )
  }

  const handlePipelineStageChange = (
    candidateId: Id<'candidates'>,
    stage: PipelineStage,
  ): void => {
    if (stage === 'rejected' && rejectReasons.length > 0) {
      setSelectedRejectReasonId(undefined)
      setRejectDialog({ open: true, candidateId })
      return
    }
    void movePipelineStage({ candidateId, pipelineStage: stage })
  }

  const handleRejectConfirm = async (): Promise<void> => {
    if (!rejectDialog.open || !selectedRejectReasonId) return
    await rejectCandidateWithReason({
      candidateId: rejectDialog.candidateId,
      rejectReasonId: selectedRejectReasonId,
    })
    await movePipelineStage({
      candidateId: rejectDialog.candidateId,
      pipelineStage: 'rejected',
    })
    setRejectDialog({ open: false })
    setSelectedRejectReasonId(undefined)
  }

  const handleToggleCandidateSelection = (
    candidateId: Id<'candidates'>,
  ): void => {
    setSelectedCandidateIds((prev) => {
      const next = new Set(prev)
      if (next.has(candidateId)) {
        next.delete(candidateId)
      } else if (next.size < MAX_COMPARE_CANDIDATES) {
        next.add(candidateId)
      }
      return next
    })
  }

  const handleOpenCompareDialog = (): void => {
    setCompareDialog({ open: true })
  }

  const handleCopyCompareLink = (): void => {
    const ids = Array.from(selectedCandidateIds).join(',')
    const url = `${window.location.origin}/dashboard/candidates?compare=${encodeURIComponent(ids)}`
    void navigator.clipboard.writeText(url)
  }

  const handleToggleSelectAll = (): void => {
    const allIds = (results ?? []).map((c) => c._id as Id<'candidates'>)
    if (selectedCandidateIds.size === allIds.length) {
      setSelectedCandidateIds(new Set())
    } else {
      setSelectedCandidateIds(new Set(allIds))
    }
  }

  const handleTemplateSelect = (templateId: Id<'messageTemplates'>): void => {
    setBulkTemplateId(templateId)
    const template = messageTemplates.find((t) => t._id === templateId)
    if (template) {
      setBulkSubject(template.subject)
      setBulkBody(template.body)
      setBulkEditorKey((k) => k + 1)
    }
  }

  const handleOpenBulkMessageDialog = (): void => {
    setBulkTemplateId(undefined)
    setBulkSubject('')
    setBulkBody('')
    setBulkEditorKey((k) => k + 1)
    setBulkSendResult({ status: 'idle' })
    setBulkMessageDialog({ open: true })
  }

  const handleSendBulkMessage = async (): Promise<void> => {
    if (!bulkSubject.trim() || !bulkBody.trim()) return
    const result = await sendBulkMessage({
      candidateIds: Array.from(selectedCandidateIds),
      subject: bulkSubject.trim(),
      body: bulkBody.trim(),
    })
    setBulkSendResult({
      status: 'success',
      sentCount: result.sentCount,
      failedCount: result.failedCount,
    })
    setSelectedCandidateIds(new Set())
  }

  const allResultIds = (results ?? []).map((c) => c._id as Id<'candidates'>)
  const allSelected =
    allResultIds.length > 0 && selectedCandidateIds.size === allResultIds.length
  const someSelected = selectedCandidateIds.size > 0

  return (
    <div className="flex h-full w-full flex-col px-6 py-8">
      <div className="mx-auto flex h-full w-full max-w-(--breakpoint-xl) flex-col gap-6">
        <div className="flex flex-wrap gap-3">
          <Input
            className="w-72"
            placeholder={t('candidateSearch.searchPlaceholder')}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />

          <Select
            value={selectedJobId ?? 'all'}
            onValueChange={(value) =>
              setSelectedJobId(
                value === 'all' ? undefined : (value as Id<'jobs'>),
              )
            }
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder={t('candidateSearch.filterJob')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t('candidateSearch.filterJob')}
              </SelectItem>
              {(jobs ?? []).map((job) => (
                <SelectItem key={job._id} value={job._id}>
                  {job.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={selectedStatus ?? 'all'}
            onValueChange={(value) =>
              setSelectedStatus(
                value === 'all' ? undefined : (value as CandidateStatus),
              )
            }
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder={t('candidateSearch.filterStatus')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t('candidateSearch.filterStatus')}
              </SelectItem>
              {CANDIDATE_STATUSES.map((status) => (
                <SelectItem key={status} value={status}>
                  {STATUS_LABELS[status]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {allTags.length > 0 && (
            <Select
              value={selectedTagId ?? 'all'}
              onValueChange={(value) =>
                setSelectedTagId(
                  value === 'all' ? undefined : (value as Id<'tags'>),
                )
              }
            >
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Filter by tag" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tags</SelectItem>
                {allTags.map((tag) => (
                  <SelectItem key={tag._id} value={tag._id}>
                    <span
                      className="inline-block rounded-full px-2 py-0.5 text-xs font-medium"
                      style={{
                        backgroundColor: `${tag.color}20`,
                        color: tag.color,
                      }}
                    >
                      {tag.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Input
            className="w-28"
            type="number"
            min={0}
            max={10}
            step={0.1}
            placeholder={t('candidateSearch.filterMinScore')}
            value={minScore ?? ''}
            onChange={(e) => {
              const val = e.target.value
              setMinScore(val === '' ? undefined : Number(val))
            }}
          />

          <Input
            className="w-28"
            type="number"
            min={0}
            max={10}
            step={0.1}
            placeholder={t('candidateSearch.filterMaxScore')}
            value={maxScore ?? ''}
            onChange={(e) => {
              const val = e.target.value
              setMaxScore(val === '' ? undefined : Number(val))
            }}
          />

          <Input
            className="w-40"
            type="date"
            placeholder={t('candidateSearch.filterFromDate')}
            value={
              fromAt ? (new Date(fromAt).toISOString().split('T')[0] ?? '') : ''
            }
            onChange={(e) => {
              const val = e.target.value
              setFromAt(val ? new Date(val).getTime() : undefined)
            }}
          />

          <Input
            className="w-40"
            type="date"
            placeholder={t('candidateSearch.filterToDate')}
            value={
              toAt ? (new Date(toAt).toISOString().split('T')[0] ?? '') : ''
            }
            onChange={(e) => {
              const val = e.target.value
              setToAt(val ? new Date(val).getTime() : undefined)
            }}
          />

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={handleClearFilters}>
              {t('candidateSearch.clearFilters')}
            </Button>
          )}
        </div>

        {someSelected && (
          <div className="flex items-center gap-3 rounded-md border border-border bg-secondary/50 px-4 py-2">
            <span className="text-sm text-primary/70">
              {selectedCandidateIds.size} selected
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={handleOpenBulkMessageDialog}
            >
              Send message
            </Button>
            {canCompare && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleOpenCompareDialog}
              >
                Compare ({selectedCandidateIds.size})
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedCandidateIds(new Set())}
            >
              Clear selection
            </Button>
          </div>
        )}

        {isLoading ? null : (results ?? []).length === 0 ? (
          <p className="text-sm text-primary/60">
            {t('candidateSearch.noResults')}
          </p>
        ) : (
          <div className="overflow-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary text-left">
                  <th className="px-4 py-3">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={handleToggleSelectAll}
                    />
                  </th>
                  <th className="px-4 py-3 font-medium text-primary/70">
                    {t('candidateSearch.columnName')}
                  </th>
                  <th className="px-4 py-3 font-medium text-primary/70">
                    {t('candidateSearch.columnJob')}
                  </th>
                  <th className="px-4 py-3 font-medium text-primary/70">
                    {t('candidateSearch.columnStatus')}
                  </th>
                  <th className="px-4 py-3 font-medium text-primary/70">
                    Pipeline
                  </th>
                  <th className="px-4 py-3 font-medium text-primary/70">
                    {t('candidateSearch.columnScore')}
                  </th>
                  <th className="px-4 py-3 font-medium text-primary/70">
                    {t('candidateSearch.columnDate')}
                  </th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {(results ?? []).map((candidate) => (
                  <>
                    <tr
                      key={candidate._id}
                      className="border-b border-border last:border-0 hover:bg-secondary/50"
                    >
                      <td className="px-4 py-3">
                        <Checkbox
                          checked={selectedCandidateIds.has(
                            candidate._id as Id<'candidates'>,
                          )}
                          onCheckedChange={() =>
                            handleToggleCandidateSelection(
                              candidate._id as Id<'candidates'>,
                            )
                          }
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium">{candidate.name}</p>
                          <p className="text-xs text-primary/60">
                            {candidate.email}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-primary/80">
                        {candidate.jobTitle}
                      </td>
                      <td className="px-4 py-3">
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary/70">
                          {STATUS_LABELS[candidate.status as CandidateStatus] ??
                            candidate.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Select
                          value={candidate.pipelineStage}
                          onValueChange={(value) =>
                            handlePipelineStageChange(
                              candidate._id as Id<'candidates'>,
                              value as PipelineStage,
                            )
                          }
                        >
                          <SelectTrigger className="h-7 w-36 text-xs">
                            <span
                              className={`rounded-full px-2 py-0.5 text-xs font-medium ${PIPELINE_STAGE_STYLES[candidate.pipelineStage as PipelineStage] ?? ''}`}
                            >
                              {PIPELINE_STAGE_LABELS[
                                candidate.pipelineStage as PipelineStage
                              ] ?? candidate.pipelineStage}
                            </span>
                          </SelectTrigger>
                          <SelectContent>
                            {PIPELINE_STAGES.map((stage) => (
                              <SelectItem key={stage} value={stage}>
                                <span
                                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${PIPELINE_STAGE_STYLES[stage]}`}
                                >
                                  {PIPELINE_STAGE_LABELS[stage]}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3 text-primary/80">
                        {candidate.averageScore !== null
                          ? t('candidateSearch.scoreValue', {
                              score: candidate.averageScore.toFixed(1),
                            })
                          : t('candidateSearch.noScore')}
                      </td>
                      <td className="px-4 py-3 text-primary/60 text-xs">
                        {formatDate(candidate.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {candidate.interviewId && (
                            <Link
                              to="/dashboard/interviews/$interviewId"
                              params={{ interviewId: candidate.interviewId }}
                              className="text-xs font-medium text-primary underline underline-offset-2 hover:text-primary/80"
                            >
                              {t('candidateSearch.viewReport')}
                            </Link>
                          )}
                          <button
                            type="button"
                            className="text-xs text-primary/50 hover:text-primary/80"
                            onClick={() =>
                              handleTogglePipelineHistory(
                                candidate._id as Id<'candidates'>,
                              )
                            }
                          >
                            {expandedCandidateId === candidate._id
                              ? 'Hide history'
                              : 'History'}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedCandidateId === candidate._id && (
                      <tr
                        key={`${candidate._id}-history`}
                        className="border-b border-border bg-secondary/20"
                      >
                        <td colSpan={9} className="px-4 py-3">
                          <div className="grid grid-cols-6 gap-6">
                            <PipelineStageTimeline
                              candidateId={candidate._id as Id<'candidates'>}
                            />
                            <CandidateNotesThread
                              candidateId={candidate._id as Id<'candidates'>}
                            />
                            <CandidateTagsEditor
                              candidateId={candidate._id as Id<'candidates'>}
                            />
                            <CandidateTodosPanel
                              candidateId={candidate._id as Id<'candidates'>}
                            />
                            <CandidateOfferPanel
                              candidateId={candidate._id as Id<'candidates'>}
                            />
                            <CandidateReferralSource
                              candidateId={candidate._id as Id<'candidates'>}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog
        open={rejectDialog.open}
        onOpenChange={(open) => {
          if (!open) setRejectDialog({ open: false })
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject candidate</DialogTitle>
            <DialogDescription>
              Select a reason for rejecting this candidate.
            </DialogDescription>
          </DialogHeader>
          <Select
            value={selectedRejectReasonId ?? ''}
            onValueChange={(value) =>
              setSelectedRejectReasonId(value as Id<'rejectReasons'>)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a reason..." />
            </SelectTrigger>
            <SelectContent>
              {rejectReasons.map((reason) => (
                <SelectItem key={reason._id} value={reason._id}>
                  {reason.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <DialogClose render={<Button variant="ghost" />}>
              Cancel
            </DialogClose>
            <Button
              onClick={() => void handleRejectConfirm()}
              disabled={!selectedRejectReasonId}
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={bulkMessageDialog.open}
        onOpenChange={(open) => {
          if (!open) setBulkMessageDialog({ open: false })
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send message</DialogTitle>
            <DialogDescription>
              Send an email to {selectedCandidateIds.size} selected candidate
              {selectedCandidateIds.size === 1 ? '' : 's'}. Use{' '}
              <code className="rounded bg-primary/5 px-1 text-xs">
                {'{{candidateName}}'}
              </code>{' '}
              and{' '}
              <code className="rounded bg-primary/5 px-1 text-xs">
                {'{{jobTitle}}'}
              </code>{' '}
              as placeholders.
            </DialogDescription>
          </DialogHeader>

          {bulkSendResult.status === 'success' ? (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-primary">
                Sent {bulkSendResult.sentCount} email
                {bulkSendResult.sentCount === 1 ? '' : 's'} successfully.
                {bulkSendResult.failedCount > 0 &&
                  ` ${bulkSendResult.failedCount} failed.`}
              </p>
              <DialogFooter>
                <DialogClose render={<Button />}>Done</DialogClose>
              </DialogFooter>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {messageTemplates.length > 0 && (
                <Select
                  value={bulkTemplateId ?? ''}
                  onValueChange={(value) =>
                    handleTemplateSelect(value as Id<'messageTemplates'>)
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Load from template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {messageTemplates.map((template) => (
                      <SelectItem key={template._id} value={template._id}>
                        {template.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Input
                placeholder="Subject..."
                value={bulkSubject}
                onChange={(e) => setBulkSubject(e.target.value)}
              />
              <RichTextEditor
                key={bulkEditorKey}
                initialContent={bulkBody}
                onChange={setBulkBody}
                placeholder="Message body..."
                minHeightClassName="min-h-32"
              />
              <DialogFooter>
                <DialogClose render={<Button variant="ghost" />}>
                  Cancel
                </DialogClose>
                <Button
                  onClick={() => void handleSendBulkMessage()}
                  disabled={
                    !bulkSubject.trim() || !bulkBody.trim() || isSendingBulk
                  }
                >
                  {isSendingBulk
                    ? 'Sending...'
                    : `Send to ${selectedCandidateIds.size}`}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={compareDialog.open}
        onOpenChange={(open) => {
          if (!open) setCompareDialog({ open: false })
        }}
      >
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Compare candidates</DialogTitle>
            <DialogDescription>
              Side-by-side comparison of {comparisonData.length} candidate
              {comparisonData.length === 1 ? '' : 's'}.
            </DialogDescription>
          </DialogHeader>

          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: `repeat(${comparisonData.length || 1}, 1fr)`,
            }}
          >
            {comparisonData.map((entry) => (
              <div
                key={entry._id}
                className="flex flex-col gap-3 rounded-md border border-border p-4"
              >
                <div>
                  <p className="font-semibold text-primary">{entry.name}</p>
                  <p className="text-xs text-primary/60">{entry.email}</p>
                </div>

                <div>
                  <p className="text-xs font-medium text-primary/50 uppercase tracking-wide mb-1">
                    Pipeline stage
                  </p>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${PIPELINE_STAGE_STYLES[entry.pipelineStage as PipelineStage] ?? 'bg-gray-100 text-gray-700'}`}
                  >
                    {PIPELINE_STAGE_LABELS[
                      entry.pipelineStage as PipelineStage
                    ] ?? entry.pipelineStage}
                  </span>
                </div>

                <div>
                  <p className="text-xs font-medium text-primary/50 uppercase tracking-wide mb-1">
                    Score
                  </p>
                  <p className="text-sm text-primary">
                    {entry.averageScore !== null
                      ? entry.averageScore.toFixed(1)
                      : '—'}
                  </p>
                </div>

                {entry.competencyScores.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-primary/50 uppercase tracking-wide mb-1">
                      Competencies
                    </p>
                    <ul className="flex flex-col gap-1">
                      {entry.competencyScores.map((cs) => (
                        <li
                          key={cs.competency}
                          className="flex items-center justify-between text-xs text-primary/80"
                        >
                          <span className="truncate">{cs.competency}</span>
                          <span className="ml-2 font-medium">
                            {cs.score.toFixed(1)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div>
                  <p className="text-xs font-medium text-primary/50 uppercase tracking-wide mb-1">
                    Latest note
                  </p>
                  <p className="text-xs text-primary/70 line-clamp-3">
                    {entry.latestNote ?? '—'}
                  </p>
                </div>

                {entry.interviewId && (
                  <Link
                    to="/dashboard/interviews/$interviewId"
                    params={{ interviewId: entry.interviewId }}
                    className="text-xs font-medium text-primary underline underline-offset-2 hover:text-primary/80"
                  >
                    View report
                  </Link>
                )}
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCopyCompareLink}>
              Copy link
            </Button>
            <DialogClose render={<Button variant="ghost" />}>Close</DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
